import { cn, DotSeparator } from '@seen/ui'
import {
  IconAlertCircle,
  IconChecks,
  IconCircleCheck,
  IconCircleX,
  IconClock,
} from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'

export interface PharmacyTransmissionEvent {
  action: ReactNode
  time: string
  isError: boolean
  source?: 'elation' | 'granerx'
}

export interface PharmacyTransmissionStatusIconProps {
  events: PharmacyTransmissionEvent[]
  className?: string
}

interface IconAndColor {
  icon: JSX.Element
  colorClass: string
}

export function PharmacyTransmissionStatusIcon({
  events,
  className,
}: PharmacyTransmissionStatusIconProps): JSX.Element | null {
  if (events.length === 0) {
    return null
  }

  /* Show the latest state (most recent event) */
  const latestEvent = events[0]

  /* Determine icon and color based on concordance between Elation and GraneRx */
  const getIconAndColor = (): IconAndColor => {
    const hasGraneRxSuccess = events.some(
      (e) => !e.isError && e.source === 'granerx'
    )
    const hasElationSuccess = events.some(
      (e) => !e.isError && e.source === 'elation'
    )
    const hasUnresolvedError = events.some((e) => e.isError)

    /* Both Elation and GraneRx report success — concordant ✓✓ */
    if (hasGraneRxSuccess && hasElationSuccess && !hasUnresolvedError) {
      return {
        icon: <IconChecks className="size-4" />,
        colorClass: 'text-success',
      }
    }

    /* Discrepancy: one source has error, the other has success — ⚠ */
    if (hasUnresolvedError && (hasGraneRxSuccess || hasElationSuccess)) {
      return {
        icon: <IconAlertCircle className="size-4" />,
        colorClass: 'text-amber-500',
      }
    }

    /* Only errors, no success — ✗ */
    if (hasUnresolvedError) {
      return {
        icon: <IconCircleX className="size-4" />,
        colorClass: 'text-destructive',
      }
    }

    const actionText = latestEvent.action?.toString().toLowerCase() ?? ''

    if (actionText.includes('resolved')) {
      return {
        icon: <IconCircleCheck className="size-4" />,
        colorClass: 'text-success',
      }
    }

    /* GraneRx only (no Elation success) — single check */
    if (hasGraneRxSuccess) {
      return {
        icon: <IconCircleCheck className="size-4" />,
        colorClass: 'text-muted-foreground',
      }
    }

    /* Elation/Surescripts only — single check (awaiting pharmacy confirmation) */
    if (
      actionText.includes('sent') ||
      actionText.includes('complete') ||
      actionText.includes('cancellation sent')
    ) {
      return {
        icon: <IconCircleCheck className="size-4" />,
        colorClass: 'text-muted-foreground',
      }
    }

    if (actionText.includes('pending') || actionText.includes('in progress')) {
      return {
        icon: <IconClock className="size-4" />,
        colorClass: 'text-muted-foreground',
      }
    }

    /* Default */
    return {
      icon: <IconAlertCircle className="size-4" />,
      colorClass: 'text-muted-foreground',
    }
  }

  const { icon, colorClass } = getIconAndColor()

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('inline-flex cursor-help', colorClass, className)}>
          {icon}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-semibold">Transmission History</div>
          {events.map((event, idx) => (
            <div
              key={idx}
              className={cn(
                'flex items-center gap-1 text-xs',
                event.isError ? 'text-destructive' : 'text-foreground'
              )}
            >
              {event.action}
              <DotSeparator />
              <span className="text-xs text-muted-foreground">
                <HoverDate date={event.time} format="datetime" />
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
